// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Pagination, useTheme, TextField, Stack, FormControl, InputLabel, Select, MenuItem, TablePagination, Typography, Grid, Box, Chip, IconButton, Divider, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useLocations } from '../../hooks/useLocations';
import { useTechnicians } from '../../hooks/useTechnicians';
import { getInspectionReports, deleteInspectionReport, deleteFileFromStorage, ensureBucketExists, getDownloadURL } from '../../services/supabaseService';

const SectionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
}));

const InspectionReports: React.FC = () => {
  const [reports, setReports] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewReportDialog, setViewReportDialog] = useState(false);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const locations = useLocations();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [uniqueYears, setUniqueYears] = useState([]);
  const [filters, setFilters] = useState({
    location: '',
    quarter: '',
    assignee: '',
    year: ''
  });
  const assignees = useTechnicians();

  const tableHeadStyle = theme.palette.mode === 'dark' ? {
    backgroundColor: '#555555',
    '& .MuiTableCell-head': {
      color: '#ffffff',
    }
  } : {};

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const inspectionReports = await getInspectionReports();

        // Process the reports
        let processedReports = inspectionReports.map(report => {
          const date = new Date(report.date);
          return {
            id: report.id,
            location: report.location,
            assignee: report.technician,
            machine: report.machine_name || report.machine,
            machineId: report.machine,
            date: report.date,
            quarter: `Q${Math.ceil((date.getMonth() + 1) / 3)}`,
            year: date.getFullYear().toString(),
            pdfUrl: report.pdf_url,
            checklist: report.checklist
          };
        });

        // Sort reports by date in descending order
        processedReports.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Extract unique years for filtering
        const uniqueYears = [...new Set(processedReports.map(report => report.year))].sort((a, b) => b - a);

        // Apply filters
        const filteredReports = processedReports.filter(report => {
          return (
            (!filters.location || report.location === filters.location) &&
            (!filters.quarter || report.quarter === filters.quarter) &&
            (!filters.assignee || report.assignee === filters.assignee) &&
            (!filters.year || report.year === filters.year)
          );
        });

        // Apply search term filter
        const searchTermFilteredReports = searchTerm ? filteredReports.filter(report =>
          Object.entries(report).some(([key, value]) =>
            typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
          )
        ) : filteredReports;

        setReports(searchTermFilteredReports);
        setUniqueYears(uniqueYears);
      } catch (error) {
        console.error("Failed to fetch inspection reports:", error);
      }
    };

    fetchReports();
  }, [searchTerm, filters]);

  const handleDownload = async (pdfUrl) => {
    if (!pdfUrl) {
      console.error("PDF URL is not available");
      alert("PDF URL is not available for this report");
      return;
    }

    try {


      // Use the exact bucket name that exists in Supabase
      const bucketName = 'Quarterly_Inspection';

      // Get a fresh download URL
      const downloadUrl = await getDownloadURL(bucketName, pdfUrl);


      // Open the URL in a new tab
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error("Failed to download PDF:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));

      // Fallback to direct URL opening if getting a fresh URL fails

      window.open(pdfUrl, '_blank');

      alert("There was an issue generating a download link. Attempting to open the file directly.");
    }
  };

  const handleDelete = async () => {
    if (selectedReport) {
      try {
        // If the report has a PDF URL, try to delete the file from storage first
        if (selectedReport.pdfUrl) {
          try {
            // Use the exact bucket name that exists in Supabase
            const bucketName = 'Quarterly_Inspection';

            // Delete the file from storage - pass the full URL to let the function extract the path
            await deleteFileFromStorage(bucketName, selectedReport.pdfUrl);
          } catch (storageError) {
            console.error('Failed to delete file from storage:', storageError);
            console.error('Error details:', JSON.stringify(storageError, null, 2));
            // Continue even if file deletion fails
          }
        }

        // Delete the report from Supabase database
        await deleteInspectionReport(selectedReport.id);

        // Update the UI by removing the deleted report
        setReports(reports.filter(report => report.id !== selectedReport.id));
        setOpenDialog(false);
      } catch (error) {
        console.error("Failed to delete inspection report:", error);
        alert("Failed to delete the report. Please try again.");
      }
    }
  };

  const handleOpenDialog = (report) => {
    setSelectedReport(report);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewReportDialog(true);
  };

  const handleCloseViewReport = () => {
    setViewReportDialog(false);
  };

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const ReportsTable = ({ reports }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const indexOfLastReport = (page + 1) * rowsPerPage;
    const indexOfFirstReport = indexOfLastReport - rowsPerPage;
    const currentReports = reports.slice(indexOfFirstReport, indexOfLastReport);

    return (
      <>
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead sx={tableHeadStyle}>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell>Assignee</TableCell>
                <TableCell>Machine</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Quarter</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentReports.map((report) => (
                <TableRow
                  key={report.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                  onClick={() => handleViewReport(report)}
                >
                  <TableCell component="th" scope="row">
                    {report.location}
                  </TableCell>
                  <TableCell>{report.assignee}</TableCell>
                  <TableCell>{report.machine}</TableCell>
                  <TableCell>{report.date}</TableCell>
                  <TableCell>{report.quarter}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      onClick={() => handleViewReport(report)}
                      sx={{ color: theme.palette.info.main }}
                    >
                      <InfoIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDownload(report.pdfUrl)}
                      sx={{ color: theme.palette.success.main }}
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDialog(report)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={reports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </>
    );
  };

  const handleFilterOpen = () => setFilterOpen(true);

  // Function to render checklist items in the view dialog
  const renderChecklistItems = (checklist) => {
    if (!checklist) return null;

    // Group checklist items by section
    const sections = {};
    Object.entries(checklist).forEach(([key, value]) => {
      // Extract section from key (assuming format like "SECTION - Item")
      const sectionMatch = key.match(/^([A-Z\s]+)(?:\s-\s|\s)/);
      const section = sectionMatch ? sectionMatch[1].trim() : 'Other';

      if (!sections[section]) {
        sections[section] = [];
      }

      sections[section].push({ key, value });
    });

    return Object.entries(sections).map(([section, items]) => (
      <Box key={section} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          {section}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {items.map(({ key, value }) => (
          <Box key={key} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
              {key.replace(/^[A-Z\s]+(?:\s-\s|\s)/, '')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <Chip
                label={value.yes ? "Yes" : "No"}
                color={value.yes ? "success" : "error"}
                size="small"
                variant="outlined"
              />
              {value.comment && (
                <Typography variant="body2" color="text.secondary">
                  Comment: {value.comment}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    ));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Inspection Reports
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Access and manage quarterly machine inspection reports and documentation.
        </Typography>
      </Box>
      <Paper style={{ padding: '20px', marginBottom: '20px' }} elevation={3}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <TextField
            fullWidth
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchTermChange}
          />
          <Button
            variant="contained"
            onClick={handleFilterOpen}
            color="primary"
          >
            Filter
          </Button>
        </Stack>
      </Paper>
      <ReportsTable reports={reports} />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Confirm Deletion
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the inspection report for {selectedReport?.machine} at {selectedReport?.location}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Report Dialog */}
      <Dialog
        open={viewReportDialog}
        onClose={handleCloseViewReport}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              Inspection Report Details
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseViewReport} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          {selectedReport && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Location</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>{selectedReport.location}</Typography>

                <Typography variant="subtitle2" color="text.secondary">Machine</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>{selectedReport.machine}</Typography>

                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>{selectedReport.date}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Technician</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>{selectedReport.assignee}</Typography>

                <Typography variant="subtitle2" color="text.secondary">Quarter</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>{selectedReport.quarter} {selectedReport.year}</Typography>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  {selectedReport.pdfUrl && (
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(selectedReport.pdfUrl)}
                      color="primary"
                    >
                      Download PDF
                    </Button>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Inspection Checklist</Typography>
                <Box sx={{ maxHeight: '40vh', overflow: 'auto', pr: 1 }}>
                  {renderChecklistItems(selectedReport.checklist)}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseViewReport} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} aria-labelledby="filter-dialog-title" maxWidth="sm" fullWidth={true}>
        <DialogTitle id="filter-dialog-title">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Filter Reports
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setFilterOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Location</InputLabel>
            <Select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              label="Location"
            >
              {locations.map((loc) => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Quarter</InputLabel>
            <Select
              value={filters.quarter}
              onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
              label="Quarter"
            >
              {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => (
                <MenuItem key={quarter} value={quarter}>{quarter}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filters.assignee}
              onChange={(e) => setFilters({ ...filters, assignee: e.target.value })}
              label="Assignee"
            >
              {assignees.map((assignee) => (
                <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Dynamically generated year options for filtering */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Year</InputLabel>
            <Select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              label="Year"
            >
              {uniqueYears.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFilterOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={() => setFilterOpen(false)} variant="contained" color="primary">Apply</Button>
          <Button
            onClick={() => {
              setFilters({ location: '', quarter: '', assignee: '', year: '' });
            }}
            color="warning"
            variant="outlined"
          >
            Clear Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InspectionReports;